import { useMutation, useQuery } from "@tanstack/react-query";
import { AvForm, AvField } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { toast } from "react-toastify";
import {
    Button,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Row,
    Col,
    Label
} from "reactstrap";
import Select from "react-select";
import ProcessingActivityService from "src/modules/data-protection/apis/ProcessingActivityService";
import CommonService from "src/modules/data-protection/apis/CommonService";
import { useState } from "react";

const CreationModal = ({
    t,
    toggleModal,
    modalIsOpen,
    software,
    onCreationSuccess
}) => {
    const [ selectedResponsiblePerson, setSelectedResponsiblePerson ] = useState({
        value: software?.responsible_person?.id,
        label: software?.responsible_person?.full_name
    });

    const {
        mutate: createActivity,
        isLoading: activityCreationIsLoading
    } = useMutation({
        mutationFn: async (payload) => {
            const service = ProcessingActivityService.getInstance();
            return await service.createActivity(payload)
        },
        onSuccess() {
            toast(t("Processing activity created successfully."), {
                type: 'success'
            });
            toggleModal();
            onCreationSuccess && onCreationSuccess();
        },
        onError(error) {
            if (process.env.NODE_ENV === 'development') console.error(error)
            toast(t('Failed to create the processing activity. Please try again.'), {
                type: "error"
            })
        }
    });

    const {
        data: responsiblePeopleList,
        isFetching: responsiblePeopleListIsFetching
    } = useQuery({
        queryKey: ['gdpr-fetch-tasks-owners-list'],
        queryFn: async () => {
            const service = CommonService.getInstance();

            const result = await service.fetchTaskOwners();

            return (result || []).map((u) => {
                return {
                    value: u.id,
                    label: `${u.first_name} ${u.last_name}`,
                };
            })
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching users."), {
                type: "error",
            });
        },
    });

    const handleSubmit = (_e, values) => {
        createActivity({
            ...values,
            software: software.id
        });
    }

    return (
        <Modal
            isOpen={modalIsOpen}
            toggle={toggleModal}
        >
            <AvForm
                className="needs-validation"
                onValidSubmit={handleSubmit}
            >
                <ModalHeader
                    toggle={toggleModal}
                >
                    {t("Create New Processing Activity")}
                </ModalHeader>
                <ModalBody>
                    <Row>
                        <Col sm="12" className="mb-3">
                            <Label htmlFor="activity-title-input">
                                {t("Title")}
                            </Label>
                            <AvField
                                id="activity-title-input"
                                name="moduleName"
                                type="text"
                                errorMessage={t("This field cannot be blank")}
                                className="form-control"
                                validate={{
                                    required: { value: true }
                                }}
                            />
                        </Col>

                        <Col sm="12">
                            <Label htmlFor="activity-responsible-person-input">
                                {t("Responsible Person")}
                            </Label>
                            <Select
                                id="activity-responsible-person-input"
                                placeholder={t("Select") + "..."}
                                classNamePrefix="select2-selection"
                                options={responsiblePeopleList}
                                menuPortalTarget={document.body}
                                isLoading={responsiblePeopleListIsFetching}
                                isDisabled={responsiblePeopleListIsFetching}
                                styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                                onChange={(e) => setSelectedResponsiblePerson(e)}
                                defaultValue={selectedResponsiblePerson}
                            />
                            <AvField 
                                type="hidden"
                                name="responsiblePerson"
                                value={selectedResponsiblePerson?.value}
                                validate={{
                                    required: { value: true }
                                }}
                                errorMessage={t("Please select a responsible person for this activity")}
                            />
                        </Col>
                    </Row>
                </ModalBody>

                <ModalFooter>
                    <Button
                        color="secondary"
                        onClick={toggleModal}
                        className="me-2"
                    >
                        {t("Cancel")}
                    </Button>

                    <Button
                        color="primary"
                        disabled={activityCreationIsLoading}
                    >
                        {activityCreationIsLoading && (
                            <Spinner
                                size='sm'
                                className="me-1"
                            />
                        )}
                        {t("Save")}
                    </Button>
                </ModalFooter>
            </AvForm>
        </Modal>
    )
}

export default withNamespaces()(
    CreationModal
);